import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { hideSnackBar, markSnackBarExiting, executeUndo, showPendingNotification } from '../../redux/Slices/snackbarSlice';
import { executeUndoAction, cleanupUndoAction } from '../../utils/snackbarHelpers';
import './scss/SnackBar.scss';
import Spinner from './Spinner';
import CircleCheckOutlined from '../icons/CircleCheckOutlined';
import Warning from '../icons/Warning';
import Error from '../icons/Error';
import { Close, Info, Upload } from '../icons';

const SnackBarItem = ({ notification, onClose, onUndo }) => {
  const [isExiting, setIsExiting] = useState(notification.isExiting || false);
  const [timeLeft, setTimeLeft] = useState(notification.duration);

  // Handle external isExiting state changes from Redux
  useEffect(() => {
    if (notification.isExiting && !isExiting) {
      setIsExiting(true);
      setTimeout(() => onClose(notification.id), 300);
    }
  }, [notification.isExiting, isExiting, notification.id, onClose]);

  useEffect(() => {
    if (notification.persistent || isExiting) return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 100) {
          handleClose();
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [notification.persistent, isExiting]);

  const handleClose = useCallback(() => {
    if (isExiting) return; // Prevent multiple close calls
    setIsExiting(true);
    setTimeout(() => onClose(notification.id), 300);
  }, [notification.id, onClose, isExiting]);

  const handleUndo = useCallback(() => {
    if (notification.hasUndo) {
      onUndo(notification.id);
    }
  }, [notification.id, notification.hasUndo, onUndo]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CircleCheckOutlined size={20} className="snackbar-icon" />;
      case 'error':
        return <Error size={20} className="snackbar-icon" />;
      case 'warning':
        return <Warning size={20} className="snackbar-icon" />;
      case 'uploading':
        const uploadPercentage = notification.uploadProgress?.percentage || '0';
        const isCompleted = parseFloat(uploadPercentage) >= 100;

        return (
          <div className="icon-container">
            {isCompleted ? (
              <CircleCheckOutlined size={20} className="snackbar-icon" />
            ) : (
              <>
                <Upload size={20} className="snackbar-icon" />
                <Spinner size={20} color="var(--brand-color)" className="uploading-spinner" strokeWidth={3} speed={2.5} />
              </>
            )}
          </div>
        );
      default:
        return <Info size={20} className="snackbar-icon" />;
    }
  };

  const progressPercentage = notification.persistent ? 100 : (timeLeft / notification.duration) * 100;
  const uploadPercentage = notification.uploadProgress?.percentage || '0';
  const remainingTime = notification.uploadProgress?.remainingTime || '';
  const isUploading = notification.type === 'uploading';

  return (
    <div className={`snackbar-item snackbar-${notification.type} ${isExiting ? 'snackbar-exit' : ''}`}>
      <div className="snackbar-content">
        {getIcon()}

        <div className="snackbar-message-container">
          <span className="snackbar-message">{notification.message}</span>
          {isUploading && (
            <div className="upload-details">
              <div className="upload-percentage">{uploadPercentage}%</div>
              {remainingTime && remainingTime !== 'Completed' && (
                <div className="upload-time">ETA: {remainingTime}</div>
              )}
            </div>
          )}
        </div>

        {notification.hasUndo && (
          <button className="snackbar-undo-btn" onClick={handleUndo}>
            {notification.undoLabel || 'Undo'}
          </button>
        )}

        <button className="snackbar-close-btn" onClick={handleClose}>
          <Close size={20} />
        </button>
      </div>

      {isUploading ? (
        <div className="snackbar-progress upload-progress">
          <div
            className="snackbar-progress-fill upload-progress-fill"
            style={{ width: `${uploadPercentage}%` }}
          />
        </div>
      ) : !notification.persistent && (
        <div className="snackbar-progress">
          <div
            className="snackbar-progress-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      )}
    </div>
  );
};

const SnackBar = () => {
  const dispatch = useDispatch();
  const { notifications, pendingNotification } = useSelector((state) => state.snackbar);

  const handleClose = useCallback((id) => {
    // Cleanup undo action if it exists
    cleanupUndoAction(id);
    dispatch(hideSnackBar({ id }));

    // After closing, show pending notification if exists
    setTimeout(() => {
      dispatch(showPendingNotification());
    }, 50); // Small delay to ensure smooth transition
  }, [dispatch]);

  const handleUndo = useCallback((id) => {
    // Execute the undo action stored outside Redux
    executeUndoAction(id);
    dispatch(executeUndo({ id }));

    // After undo, show pending notification if exists
    setTimeout(() => {
      dispatch(showPendingNotification());
    }, 50);
  }, [dispatch]);

  if (!notifications.length) return null;

  return (
    <div className="snackbar-container">
      {notifications.map((notification) => (
        <SnackBarItem
          key={notification.id}
          notification={notification}
          onClose={handleClose}
          onUndo={handleUndo}
        />
      ))}
    </div>
  );
};

export default SnackBar;
